import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faTrophy,
  faGraduationCap,
  faShareAlt,
  faUsers,
  faPenFancy,
  faGlobe,
  faCalendarCheck,
  faBullseye,
  faMinusCircle,
} from '@fortawesome/free-solid-svg-icons';

const TopOfTheLot = () => {
  const [activeTab, setActiveTab] = useState('about');

  const tabs = [
    { id: 'about', label: 'About', icon: faInfoCircle },
    { id: 'leaderboard', label: 'Leader Board', icon: faTrophy },
    { id: 'academic', label: 'Academic Effort Score', icon: faGraduationCap },
    { id: 'social', label: 'Social Sharing Score', icon: faShareAlt },
    { id: 'friends', label: 'Friends Circle Score', icon: faUsers },
    { id: 'writing', label: 'Writing & Thinking Score', icon: faPenFancy },
    { id: 'digital', label: 'Digital Reach Score', icon: faGlobe },
    { id: 'consistency', label: 'Consistency Score', icon: faCalendarCheck },
    { id: 'intent', label: 'Intent & Purpose Score', icon: faBullseye },
    { id: 'negative', label: 'Negative Score', icon: faMinusCircle },
  ];

  // Dummy student data with varied scores (0–10 scale for each category)
  const dummyStudents = [
    { name: 'Aarav Sharma', city: 'Mumbai', stream: 'JEE Prep', academic: 9.5, social: 8.0, friends: 9.2, writing: 7.5, digital: 7.0, consistency: 9.8, intent: 9.5, negative: 0.2 },
    { name: 'Priya Patel', city: 'Delhi', stream: 'NEET', academic: 8.8, social: 9.5, friends: 8.5, writing: 9.0, digital: 8.5, consistency: 8.7, intent: 9.0, negative: 0.5 },
    { name: 'Rohan Kumar', city: 'Bangalore', stream: 'Engineering', academic: 9.0, social: 7.5, friends: 9.8, writing: 8.0, digital: 9.0, consistency: 9.0, intent: 8.5, negative: 1.0 },
    { name: 'Ananya Singh', city: 'Chennai', stream: 'Medical', academic: 8.5, social: 9.2, friends: 8.0, writing: 9.5, digital: 7.5, consistency: 8.5, intent: 9.8, negative: 0.0 },
    { name: 'Vikram Reddy', city: 'Hyderabad', stream: 'PhD', academic: 9.2, social: 8.5, friends: 7.5, writing: 9.8, digital: 8.0, consistency: 9.5, intent: 9.2, negative: 0.8 },
    { name: 'Isha Gupta', city: 'Pune', stream: 'Engineering', academic: 8.0, social: 9.8, friends: 9.0, writing: 8.5, digital: 9.5, consistency: 8.0, intent: 8.8, negative: 0.3 },
    { name: 'Arjun Mehta', city: 'Kolkata', stream: 'JEE Prep', academic: 9.8, social: 7.0, friends: 8.8, writing: 7.0, digital: 6.5, consistency: 9.9, intent: 9.0, negative: 0.0 },
    { name: 'Sanya Joshi', city: 'Ahmedabad', stream: 'NEET', academic: 8.7, social: 8.8, friends: 9.5, writing: 8.8, digital: 8.0, consistency: 8.2, intent: 8.7, negative: 1.5 },
    { name: 'Kabir Verma', city: 'Jaipur', stream: 'Engineering', academic: 9.0, social: 8.2, friends: 8.2, writing: 9.2, digital: 9.2, consistency: 8.8, intent: 9.5, negative: 0.4 },
    { name: 'Neha Rao', city: 'Lucknow', stream: 'PhD', academic: 8.3, social: 9.0, friends: 7.8, writing: 9.0, digital: 8.8, consistency: 9.2, intent: 9.0, negative: 0.7 },
  ];

  const calculateTotal = (student) => {
    const positiveSum = student.academic + student.social + student.friends + student.writing + student.digital + student.consistency + student.intent;
    const averagePositive = positiveSum / 7;
    return Math.round((averagePositive - student.negative) * 10) / 10; // Rounded to 1 decimal
  };

  const getTopScorers = (tabId) => {
    let sorted = [...dummyStudents];
    if (tabId === 'leaderboard') {
      sorted.sort((a, b) => calculateTotal(b) - calculateTotal(a));
    } else if (tabId === 'negative') {
      sorted.sort((a, b) => a.negative - b.negative);
    } else {
      const keyMap = {
        academic: 'academic',
        social: 'social',
        friends: 'friends',
        writing: 'writing',
        digital: 'digital',
        consistency: 'consistency',
        intent: 'intent',
      };
      const key = keyMap[tabId];
      sorted.sort((a, b) => b[key] - a[key]);
    }
    return sorted.slice(0, 8);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Your input has been submitted (demo mode – no real backend)!');
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="mx-auto px-4 py-2">
        <h1 className="text-lg lg:text-xl font-bold text-center mb-2 text-yellow-200 border-b border-blue-400 py-2">
          🔥 TopOfTheLot 2026
        </h1>

        <div className="grid grid-cols-3 lg:grid-cols-5 gap-2  border-gray-600 place-items-center">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center justify-center w-full px-1 py-1 rounded-md text-xs lg:text-sm font-medium transition ${
                activeTab === tab.id
                  ? "bg-teal-700 text-yellow-200"
                  : "bg-gray-800 text-gray-200 hover:bg-gray-700"
              }`}
              aria-pressed={activeTab === tab.id}
            >
              <span className="text-sm lg:text-lg"><FontAwesomeIcon icon={tab.icon}/></span>
              <span className="mt-1 text-center text-xs lg:text-sm">{tab.label}</span>
              {activeTab === tab.id && (
                <div className="hidden lg:block absolute inset-x-0 -bottom-px h-1 bg-blue-600" />
              )}              
            </button>
          ))}
        </div>
        
        {/* Tab Content */}
        <div className="bg-black rounded-xl shadow-lg p-6 sm:p-8">
          {activeTab === 'about' && (
            <div className="space-y-8 text-white">
              <div>
                <h2 className="text-lg lg:text-xl font-bold mb-4 text-yellow-200">What is TopOfTheLot?</h2>
                <p className="text-sm lg:text-lg leading-relaxed text-white">
                  TopOfTheLot is India's first peer-validated student recognition platform. It celebrates visible effort, knowledge sharing, and positive resonance — not just marks or authority-driven rankings.
                </p>
                <p className="text-sm lg:text-lg leading-relaxed mt-4 text-white">
                  It's an annual digital festival (like Spotify Wrapped for students) where peers validate real contribution and growth.
                </p>
              </div>

              <div>
                <h2 className="text-lg lg:text-xl font-bold mb-2 text-yellow-200">About TopOfTheLot</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-gray-900 border border-cyan-400 p-5 rounded-xl shadow-md">
                        <p className="font-bold text-cyan-400 mb-2">Who</p>
                        <p className="text-gray-200 leading-relaxed">
                            Ambitious Indian students (Engineering, Medical, JEE/NEET prep, PhD, professionals).
                        </p>
                    </div>
                    <div className="bg-gray-900 border border-cyan-400 p-5 rounded-xl shadow-md">
                        <p className="font-bold text-cyan-400 mb-2">What</p>
                        <p className="text-gray-200 leading-relaxed">
                            A peer-rated annual recognition list highlighting effort, sharing, and impact.
                        </p>
                    </div>
                    <div className="bg-gray-900 border border-cyan-400 p-5 rounded-xl shadow-md">
                        <p className="font-bold text-cyan-400 mb-2">When</p>
                        <p className="text-gray-200 leading-relaxed">
                            Annual cycle — TopOfTheLot 2026 is the current edition.
                        </p>
                    </div>
                    <div className="bg-gray-900 border border-cyan-400 p-5 rounded-xl shadow-md">
                        <p className="font-bold text-cyan-400 mb-2">Where</p>
                        <p className="text-gray-200 leading-relaxed">
                            Online, open to students across India.
                        </p>
                    </div>
                    <div className="bg-gray-900 border border-cyan-400 p-5 rounded-xl shadow-md">
                        <p className="font-bold text-cyan-400 mb-2">Why</p>
                        <p className="text-gray-200 leading-relaxed">
                            To give recognition through social proof and peer validation — emotionally shareable and youth-relevant.
                        </p>
                    </div>
                    <div className="bg-gray-900 border border-cyan-400 p-5 rounded-xl shadow-md">
                        <p className="font-bold text-cyan-400 mb-2">How</p>
                        <p className="text-gray-200 leading-relaxed">
                            Simple self-declaration + friend validation on transparent criteria. No heavy verification.
                        </p>
                    </div>
                </div>
              </div>

            <div className="[&_p]:text-white [&_h2]:text-yellow-200 [&_h3]:text-cyan-200">
                <h2 className="text-lg lg:text-xl font-bold mb-2">The Scoring Categories</h2>
                <div className="space-y-6">
                  <div className="border-l-4 border-blue-600 pl-6">
                    <h3 className="font-semibold text-sm lg:text-lg text-yellow-200">Leader Board</h3>
                    <p className='text-sm lg:text-lg'>Overall ranking combining all positive scores (minus negative deductions).</p>
                    <p className='text-sm lg:text-lg'>Showcases India's most resonant and effort-driven students for 2026.</p>
                  </div>
                  <div className="border-l-4 border-blue-600 pl-6">
                    <h3 className="font-semibold text-sm lg:text-lg text-yellow-200">Academic Effort Score</h3>
                    <p className='text-sm lg:text-lg'>Measures improvement and consistency in studies, not absolute marks.</p>
                    <p className='text-sm lg:text-lg'>Focuses on personal progress to reduce cheating incentives.</p>
                  </div>
                  <div className="border-l-4 border-blue-600 pl-6">
                    <h3 className="font-semibold text-sm lg:text-lg text-yellow-200">Social Sharing Score</h3>
                    <p className='text-sm lg:text-lg'>Rewards creating and sharing educational content (reels, shorts, threads).</p>
                    <p className='text-sm lg:text-lg'>Peers validate if the content genuinely helped them.</p>
                  </div>
                  <div className="border-l-4 border-blue-600 pl-6">
                    <h3 className="font-semibold text-sm lg:text-lg text-yellow-200">Friends Circle Score</h3>
                    <p className='text-sm lg:text-lg'>Measures trust and helpfulness in your peer circle.</p>
                    <p className='text-sm lg:text-lg'>Simple question: "Would you study/learn from this person?"</p>
                  </div>
                  <div className="border-l-4 border-blue-600 pl-6">
                    <h3 className="font-semibold text-sm lg:text-lg text-yellow-200">Writing & Thinking Score</h3>
                    <p className='text-sm lg:text-lg'>Rewards clear articles, blogs, notes, or posts.</p>
                    <p className='text-sm lg:text-lg'>Peers rate clarity and insight, not perfect grammar.</p>
                  </div>
                  <div className="border-l-4 border-blue-600 pl-6">
                    <h3 className="font-semibold text-sm lg:text-lg text-yellow-200">Digital Reach Score</h3>
                    <p className='text-sm lg:text-lg'>Range-based follower count with peer check for organic growth.</p>
                    <p className='text-sm lg:text-lg'>Rewards meaningful reach, not just fame.</p>
                  </div>
                  <div className="border-l-4 border-blue-600 pl-6">
                    <h3 className="font-semibold text-sm lg:text-lg text-yellow-200">Consistency Score</h3>
                    <p className='text-sm lg:text-lg'>Rewards showing up regularly (daily/weekly).</p>
                    <p className='text-sm lg:text-lg'>A silent moat — separates serious students from others.</p>
                  </div>
                  <div className="border-l-4 border-blue-600 pl-6">
                    <h3 className="font-semibold text-sm lg:text-lg text-yellow-200">Intent & Purpose Score</h3>
                    <p className='text-sm lg:text-lg'>Based on your 3-line statement of why you do what you do.</p>
                    <p className='text-sm lg:text-lg'>Peers validate seriousness and authenticity.</p>
                  </div>
                  <div className="border-l-4 border-red-600 pl-6">
                    <h3 className="font-semibold text-sm lg:text-lg text-red-600">Negative Score</h3>
                    <p className='text-sm lg:text-lg'>Deducted for reported negative behaviors (toxicity, plagiarism, etc.).</p>
                    <p className='text-sm lg:text-lg'>Keeps the platform positive and fair.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reusable pattern for score tabs */}
          {activeTab !== 'about' && activeTab !== 'leaderboard' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl lg:text-2xl font-bold mb-3 text-yellow-200">
                    {tabs.find((t) => t.id === activeTab)?.label}
                  </h2>
                  {activeTab === 'academic' && (
                    <p className="text-gray-100 leading-relaxed">
                      Measures improvement and consistency in studies, not absolute marks. Focuses on personal progress to reduce cheating incentives.
                    </p>
                  )}
                  {activeTab === 'social' && (
                    <p className="text-gray-100 leading-relaxed">
                      Rewards creating educational reels, shorts, or threads that help others. Peers validate usefulness.
                    </p>
                  )}
                  {activeTab === 'friends' && (
                    <p className="text-gray-100 leading-relaxed">
                      Measures trust and collaboration. Simple peer question: "Would you study/learn from this person?"
                    </p>
                  )}
                  {activeTab === 'writing' && (
                    <p className="text-gray-100 leading-relaxed">
                      Rewards clear articles, blogs, or shared notes. Peers rate insight and clarity.
                    </p>
                  )}
                  {activeTab === 'digital' && (
                    <p className="text-gray-100 leading-relaxed">
                      Range-based reach with peer check for organic, meaningful followers.
                    </p>
                  )}
                  {activeTab === 'consistency' && (
                    <p className="text-gray-100 leading-relaxed">
                      Rewards regular effort (daily/weekly). Separates serious students from occasional ones.
                    </p>
                  )}
                  {activeTab === 'intent' && (
                    <p className="text-gray-100 leading-relaxed">
                      Based on your authentic statement of purpose. Peers validate seriousness.
                    </p>
                  )}
                  {activeTab === 'negative' && (
                    <p className="text-gray-100 leading-relaxed">
                      Deducted for any reported negative behavior. Keeps the platform positive and credible.
                    </p>
                  )}
                </div>

                <div>
                  <h3 className="text-lg lg:text-xl font-semibold mb-4 text-yellow-200">Submit Your Input</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {activeTab === 'academic' && (
                      <>
                        <div>
                          <label className="block font-medium mb-1">Describe your academic improvement/consistency</label>
                          <textarea className="w-full text-white h-20 p-3 border rounded-lg" rows="4" required />
                        </div>
                        <div>
                          <label className="block font-medium mb-1">Upload proof (optional screenshot/PDF)</label>
                          <input type="file" accept="image/*,.pdf" className="w-full" />
                        </div>
                      </>
                    )}

                    {activeTab === 'social' && (
                      <>
                        <div>
                          <label className="block font-medium mb-1 text-yellow-200">Number of posts (reels/shorts)</label>
                          <input type="number" min="0" className="w-full text-black p-3 border rounded-lg" required />
                        </div>
                        <div>
                          <label className="block font-medium mb-1 text-white">Links (one per line)</label>
                          <textarea className="w-full p-3 border rounded-lg text-white h-20" rows="4" />
                        </div>
                      </>
                    )}

                    {activeTab === 'friends' && (
                      <div>
                        <label className="block font-medium mb-1">Invite friends to validate (WhatsApp/email, comma-separated)</label>
                        <textarea className="w-full h-20 text-white p-3 border rounded-lg" rows="3" placeholder="e.g., +919876543210, friend@example.com" required />
                      </div>
                    )}

                    {activeTab === 'writing' && (
                      <div>
                        <label className="block font-medium mb-1">Links to articles/blogs/notes (one per line)</label>
                        <textarea className="w-full h-20 text-white p-3 border rounded-lg" rows="5" required />
                      </div>
                    )}

                    {activeTab === 'digital' && (
                      <>
                        <div>
                          <label className="block font-medium mb-1">Follower range</label>
                          <select className="w-64 h-10 p-1 border rounded-lg" required>
                            <option>0–500</option>
                            <option>500–2K</option>
                            <option>2K–10K</option>
                            <option>10K+</option>
                          </select>
                        </div>
                        <div>
                          <label className="block font-medium mb-1">Are followers mostly organic & meaningful?</label>
                          <select className="w-20 h-10 p-1 border rounded-lg" required>
                            <option>Yes</option>
                            <option>No</option>
                          </select>
                        </div>
                      </>
                    )}

                    {activeTab === 'consistency' && (
                      <div>
                        <label className="block font-medium mb-1">How consistent are you with your efforts?</label>
                        <select className="w-32 h-10 p-1 border rounded-lg" required>
                          <option>Daily</option>
                          <option>Weekly</option>
                          <option>Monthly</option>
                          <option>Occasionally</option>
                        </select>
                      </div>
                    )}

                    {activeTab === 'intent' && (
                      <div>
                        <label className="block font-medium mb-1">Your 3-line statement of intent & purpose</label>
                        <textarea className="w-full h-20 text-white p-3 border rounded-lg" rows="4" required />
                      </div>
                    )}

                    {activeTab === 'negative' && (
                      <div>
                        <label className="block font-medium mb-1">Any negative behaviors to self-report? (Be honest – affects final score)</label>
                        <textarea className="w-full h-20 text-white p-3 border rounded-lg" rows="3" placeholder="e.g., None" />
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition"
                    >
                      Submit for Validation
                    </button>
                  </form>
                </div>
              </div>

              {/* Right: Top Scorers Table */}
              <div>
                <h3 className="text-sm lg:text-xl font-semibold mb-2">
                  Top Scorers – {tabs.find((t) => t.id === activeTab)?.label}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full table-auto border-collapse">
                    <thead className="bg-gray-400">
                      <tr>
                        <th className="px-4 py-3 text-left">Rank</th>
                        <th className="px-4 py-3 text-left">Name</th>
                        <th className="px-4 py-3 text-left">Score</th>
                        <th className="px-4 py-3 text-left">City</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {getTopScorers(activeTab).map((student, index) => {
                        const score =
                          activeTab === 'negative'
                            ? student.negative
                            : activeTab === 'academic'
                            ? student.academic
                            : activeTab === 'social'
                            ? student.social
                            : activeTab === 'friends'
                            ? student.friends
                            : activeTab === 'writing'
                            ? student.writing
                            : activeTab === 'digital'
                            ? student.digital
                            : activeTab === 'consistency'
                            ? student.consistency
                            : student.intent;
                        return (
                          <tr key={index} className="hover:bg-gray-500">
                            <td className="px-4 py-3 font-medium">#{index + 1}</td>
                            <td className="px-4 py-3">{student.name}</td>
                            <td className="px-4 py-3 font-semibold">{score.toFixed(1)}</td>
                            <td className="px-4 py-3">{student.city}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Special full-width Leaderboard tab */}
          {activeTab === 'leaderboard' && (
            <div>
              <h2 className="text-lg lg:text-xl font-bold mb-2 text-yellow-200">Overall Leader Board – TopOfTheLot 2026</h2>
              <div className="overflow-x-auto">
                <table className="w-full table-auto border-collapse">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="px-4 py-3 text-left">Rank</th>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">Stream</th>
                      <th className="px-4 py-3 text-left">City</th>
                      <th className="px-4 py-3 text-left">Total Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {getTopScorers('leaderboard').map((student, index) => (
                      <tr key={index} className="hover:bg-gray-500">
                        <td className="px-4 py-3 font-medium">#{index + 1}</td>
                        <td className="px-4 py-3 font-semibold">{student.name}</td>
                        <td className="px-4 py-3">{student.stream}</td>
                        <td className="px-4 py-3">{student.city}</td>
                        <td className="px-4 py-3 font-bold text-blue-700">{calculateTotal(student).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopOfTheLot;